import { lazy } from 'react';

// React.lazy for route/modal chunks, with one guard the bare version can't have.
//
// THE PROBLEM IT SOLVES (Sentry PETZY-FRONTEND-B):
// Vite wraps every dynamic import as `baseModule().catch(handlePreloadError)`,
// and handlePreloadError is:
//
//   const e = new Event('vite:preloadError', { cancelable: true })
//   window.dispatchEvent(e)
//   if (!e.defaultPrevented) throw err     // ← only re-throws if nobody cancels
//
// main.jsx's listener DOES cancel it — deliberately, so a chunk failure we're
// already recovering from doesn't reach window.onerror. But cancelling means
// handlePreloadError returns undefined instead of throwing, so the `.catch()`
// RESOLVES with undefined, and `import()` hands React.lazy an undefined module.
// lazy stores it as { _status: Resolved, _result: undefined } and the next
// render dereferences it:
//
//   TypeError: undefined is not an object (evaluating 'e._result.default')
//
// which is React's own internals, matches nothing in ErrorBoundary's
// isChunkLoadError, and lands in Sentry unhandled — the exact pollution the
// preventDefault() was meant to avoid.
//
// THE FIX: window.location.reload() is asynchronous. The page keeps rendering
// until the navigation commits, and that window is all React needs to deref the
// undefined. So when we see the undefined module, we return a promise that never
// settles: React stays suspended on the route fallback for the few frames until
// the reload lands. Nothing renders, nothing throws.
//
// This only triggers on the path where main.jsx IS reloading. When it declines
// to (already reloaded once this tab), it doesn't preventDefault, Vite re-throws,
// the import rejects normally, and ErrorBoundary sees a real chunk error — that
// path is untouched and must stay that way, since a genuinely broken deploy has
// to surface rather than hang on a spinner forever.
//
// The check is `!module` and NOT "has a default export", deliberately. Vite's
// cancelled path returns exactly undefined, so falsy is the whole signal. A
// module that loaded fine but has no `export default` is a DIFFERENT bug — a
// typo in a new page — and React already reports it loudly ("Element type is
// invalid… got: undefined"). Suspending on that instead would trade a clear
// console error for a spinner that hangs forever with nothing in the log.
export default function lazyChunk(factory) {
  return lazy(() => factory().then((module) => {
    if (!module) return new Promise(() => {});
    return module;
  }));
}
